/******************************************************************************
 *
 * Copyright (c) 2019-2022 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Shivam Mishra' <shivam.mishra@wakencode.com>, June 2022
 *
 *****************************************************************************/

import React, { useEffect, useState } from "react";
import Styles from "./Verification.module.css";
import { generateVerificationCode } from "../../network";
import { verifyVerificationCode } from "../../network";
import { string } from "../../constant/string";
import Loader from "../../loader/WhiteLoader";
import GreenLoader from "../../loader/GreenLoader";
import { useAuth } from "../../context/AuthContext";
/**
 * Verification page called in Wrapper.js
 * This page send verification code to user and
 * takes user verification code and verify the code
 *
 * @param {String} setPage State for current page
 * @returns JSX for the verification page
 */
const Verification = ({ setPage, isInvited }) => {
  // State for resend button
  const [resend, setResend] = useState(false);
  // State for user entered verification code
  const [code, setCode] = useState("");
  // State for countdown
  const [countDown, setCountDown] = useState(30);
  // State for error
  const [apiError, setApiError] = useState("");
  // State for mobile number
  const [mobileNumber, setMobileNumber] = useState("");
  // State for loader in next button
  const [loading, setLoading] = useState(false);
  // State for loader in verify button
  const [resendLoading, setResendLoading] = useState(false);
  // State to set token from auth context
  const { setToken } = useAuth("");

  /**
   * Pre-populate user mobile number if present in local storage
   */
  useEffect(() => {
    // Mobile number from local storage
    let mobileNumberFromLocalStorage = localStorage.getItem("mobileNumber");
    if (mobileNumberFromLocalStorage) {
      let newMobileNumber =
        mobileNumberFromLocalStorage.substring(0, 2) +
        " " +
        mobileNumberFromLocalStorage.substring(2, 5) +
        " " +
        mobileNumberFromLocalStorage.substring(5, 8) +
        " " +
        mobileNumberFromLocalStorage.substring(8, mobileNumberFromLocalStorage.length);
      // Set state for mobile number
      setMobileNumber(newMobileNumber);
    }
    setResend(true);
    resendCountDown(30);
    // Update countdown state for every second
    setTimeout(() => {
      setResend(false);
      setCountDown(30);
    }, 30000);
    // Send verification code to user
    sendVerificationCode();
  }, []);

  /**
   * Updating state for countdown by calling recursive function
   *
   * @param {Number} count present state of countdown
   * @returns {Void}
   */
  const resendCountDown = (count) => {
    if (count <= 0) {
      return;
    }
    count = count - 1;
    setTimeout(() => {
      setCountDown(count);
      resendCountDown(count);
    }, 1000);
  };

  /**
   * Call api to genrate/send verification code
   */
  const sendVerificationCode = () => {
    //mobile number from local storage
    let mobileNumber = window.localStorage.getItem("mobileNumber");

    //create payload
    let payload = {
      mobile: mobileNumber,
    };

    // Send verification code to user
    generateVerificationCode(payload)
      .then(() => {
        setResendLoading(false);
        setResend(true);
      })
      .catch((err) => {
        if (err.response && err.response.data && err.response.data.errorDescription) {
          if (err.response.data.errorDescription == "Limit exceeded") {
            setApiError("Limit exceeded, try again after 10 minutes");
          } else if (err?.response?.status === 401) {
            setToken("");
            setPage("SignUp");
          } else {
            // Set error state
            setApiError(err.response.data.errorDescription);
          }
        }
        setResendLoading(false);
      });
  };

  /**
   * Call api to verify user entered verification code
   */
  const verifyCode = () => {
    setLoading(true);
    // Verify user entered verification code

    //mobile number from local storage
    let mobileNumber = window.localStorage.getItem("mobileNumber");

    //create payload
    let payload = {
      mobile: mobileNumber,
      code: code,
    };

    verifyVerificationCode(payload)
      .then((response) => {
        document.cookie = `token=${response.data.token}`;
        setToken(response.data.token);
        // Set current page to your details page
        setPage("PersonalDetails");
        setLoading(false);
      })
      .catch((err) => {
        if (err.response && err.response.data && err.response.data.errorDescription) {
          // Set error state
          setApiError(err.response.data.errorDescription);
        }
        setLoading(false);
      });
  };

  /**
   * Resend button handler
   *
   * @param {*} e Event for resend button click
   */
  const handleResendButtonChange = (e) => {
    e.preventDefault();
    setResendLoading(true);
    sendVerificationCode();
    setCountDown(30);
    resendCountDown(30);
    setTimeout(() => {
      setResend(false);
      setCountDown(30);
    }, 30000);
  };

  /**
   * Input handle change
   *
   * @param {MouseEvent} e Input change event
   */
  const handleVerificationCodeChange = (e) => {
    let otp = e.target.value.replace(/\D/g, "").slice(0, 6);
    if (otp === "" || /^[0-9]+$/.test(otp)) {
      setCode(otp.slice(0, 6));
    }
    setApiError("");
  };

  /**
   * Verify button handler
   *
   * @param {*} e Event for verify button click
   * @returns {Void}
   */
  const handleVerifyButtonChnage = (e) => {
    if (code.length < 6) {
      setApiError(string.INVALID_VERIFICATION_CODE);
      return;
    }
    // Verify user entered verification code
    verifyCode();
  };

  // handle click on 'Enter' button
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleVerifyButtonChnage();
    }
  };

  /**
   * Back button handler
   *
   * @param {*} e Event for Back button click
   */
  const handleBackButtonChange = (e) => {
    e.preventDefault();
    setPage("SignUp");
  };

  return (
    <div className="main">
      {/* Verification head text */}
      <div className="headText">{string.VERIFICATION_CODE}</div>
      {/* Line under head text */}
      <div className="line"></div>
      {/* Sub-heading under line */}
      <div className="text-center">
        {string.WE_HAVE_SENT_A_CODE_TO_SMS}
        <br />
        <br />
        <strong>{mobileNumber}</strong> <br />
        <br />
      </div>
      {/* Input field for verification code */}
      <div className="tenREM">
        <input
          className={apiError ? "wrongInput" : "input"}
          placeholder={string.ENTER_VERIFICATION_CODE}
          onChange={handleVerificationCodeChange}
          onKeyDown={(e) => handleKeyDown(e)}
          value={code}
        ></input>
        {apiError ? <p className="errorText">{apiError}</p> : null}
      </div>
      {/* Next button */}
      <div>
        <button className="nextButton" onClick={!loading ? handleVerifyButtonChnage : null}>
          {" "}
          {loading ? <Loader /> : string.VERIFY}
        </button>
      </div>
      {/* Resend code button */}
      <div>
        {resend ? (
          <button className={Styles.sentCode}>
            {" "}
            {string.RESEND_CODE} ({countDown})
          </button>
        ) : (
          <button className={Styles.resendCode} onClick={handleResendButtonChange}>
            {" "}
            {resendLoading ? <GreenLoader /> : string.RESEND_CODE}
          </button>
        )}
      </div>
      {/* Back button */}
      <div>
        <button className="backButton" onClick={handleBackButtonChange}>
          {string.BACK}
        </button>
      </div>
    </div>
  );
};

export default Verification;
